import React, { useState } from 'react';
import { useFamilyRole } from '../hooks/useFamilyRole';

const RecentActivityList = ({
  event,
  onEdit,
  onDelete,
  getCategoryStyles,
  formatDateTime,
  isMobile
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { canEditEvents, canDeleteEvents } = useFamilyRole();

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const getCardBackground = () => {
    if (!event.category) return "#4361ee";
    
    switch(event.category.toLowerCase()) {
      case 'work':
        return "#1c83c9";
      case 'school':
        return "#5550B7";
      case 'sport':
        return "#E63946";
      case 'life':
        return "#2A9D8F";
      default:
        return "#4361ee";
    }
  };

  const handleDelete = (e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (event.isRecurring || event.isRecurrenceInstance) {
      const deleteOption = window.confirm(
        `"${event.eventName}" is a recurring event. What would you like to delete?\n\n` +
        `• Click OK to delete the entire recurring series\n` +
        `• Click Cancel to delete only this occurrence`
      );

      if (deleteOption) {
        const originalId = event.originalEventId || event.id;
        onDelete(originalId);
      } else {
        if (event.isRecurrenceInstance) {
          alert("Deleting a single instance of a recurring event is not fully implemented yet.");
        } else {
          onDelete(event.id);
        }
      }
    } else {
      if (window.confirm(`Are you sure you want to delete "${event.eventName}"?`)) {
        onDelete(event.id);
      }
    }
  };

  const handleEdit = (e) => {
    if (e) {
      e.stopPropagation();
    }
    onEdit(event);
  };

  // List item container style - minimal, no background
  const listItemStyle = {
    borderBottom: '1px solid #f0f0f0',
    marginBottom: '0',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    backgroundColor: 'transparent'
  };

  // Condensed header style - minimal padding, no background
  const headerStyle = {
    padding: isMobile ? '12px 0 12px 24px' : '14px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease'
  };

  // Expanded content style - indented and minimal
  const expandedStyle = {
    padding: isMobile ? '0 0 12px 56px' : '0 0 14px 40px',
    backgroundColor: 'transparent',
    display: isExpanded ? 'block' : 'none'
  };

  return (
    <div style={listItemStyle} onClick={toggleExpand}>
      {/* Condensed header - always visible */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {/* Category icon - smaller and more subtle */}
          <div style={{ marginRight: '10px', opacity: 0.7 }}>
            {event.category === 'work' && (
              <img
                src="/briefcase.svg"
                alt="Work"
                width="16"
                height="16"
                style={{filter: `brightness(0) saturate(100%) invert(47%) sepia(83%) saturate(1347%) hue-rotate(175deg) brightness(94%) contrast(85%)`}}
              />
            )}
            {event.category === 'school' && (
              <img
                src="/graduation-cap.svg"
                alt="School"
                width="16"
                height="16"
                style={{filter: `brightness(0) saturate(100%) invert(33%) sepia(57%) saturate(1720%) hue-rotate(238deg) brightness(92%) contrast(88%)`}}
              />
            )}
            {event.category === 'class' && (
              <img
                src="/group.png"
                alt="Class"
                width="16"
                height="16"
                style={{filter: `brightness(0) saturate(100%) invert(33%) sepia(57%) saturate(1720%) hue-rotate(238deg) brightness(92%) contrast(88%)`}}
              />
            )}
            {event.category === 'sport' && (
              <img
                src="/tennis.svg"
                alt="Sport"
                width="16"
                height="16"
                style={{filter: `brightness(0) saturate(100%) invert(36%) sepia(51%) saturate(2335%) hue-rotate(338deg) brightness(87%) contrast(87%)`}}
              />
            )}
            {event.category === 'life' && (
              <img
                src="/circle-star.svg"
                alt="Life"
                width="16"
                height="16"
                style={{filter: `brightness(0) saturate(100%) invert(46%) sepia(72%) saturate(512%) hue-rotate(88deg) brightness(89%) contrast(91%)`}}
              />
            )}
            {(!event.category || event.category === 'other') && (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            )}
          </div>

          {/* Event title and time on same line */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <span style={{
              fontSize: isMobile ? '0.9rem' : '0.95rem',
              fontWeight: '500',
              color: '#333',
              lineHeight: '1.4'
            }}>
              {event.eventName}
              {(event.isRecurring || event.isRecurrenceInstance) && (
                <span style={{
                  marginLeft: '4px',
                  fontSize: '11px',
                  color: '#999'
                }} title="Recurring event">↻</span>
              )}
            </span>
            {/* Time displayed inline */}
            <span style={{
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              color: '#999',
              whiteSpace: 'nowrap'
            }}>
              {formatDateTime(event)}
            </span>
          </div>
        </div>

        {/* Smaller expand/collapse indicator */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#999"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            marginLeft: '8px',
            opacity: 0.6
          }}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div style={expandedStyle}>
          {/* Simple list of details */}
          <div style={{
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            color: '#666',
            lineHeight: '1.6'
          }}>
            
            {/* Participants */}
            {((event.participants && event.participants.length > 0) || event.participant) && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#999' }}>Who's involved:</span>{' '}
                {event.participants && Array.isArray(event.participants)
                  ? event.participants.map((person, index) => {
                      const displayName = event.participantNames && event.participantNames[index] 
                        ? event.participantNames[index] 
                        : person.includes('@') ? person.split('@')[0] : person;
                      return displayName;
                    }).join(', ')
                  : event.participant?.split(',').map((person, index) => {
                      const trimmedPerson = person.trim();
                      const displayName = event.participantNames && event.participantNames[index] 
                        ? event.participantNames[index] 
                        : trimmedPerson.includes('@') ? trimmedPerson.split('@')[0] : trimmedPerson;
                      return displayName;
                    }).join(', ')
                }
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#999' }}>Description:</span> {event.description}
              </div>
            )}

            {/* Location */}
            {event.location && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#999' }}>Location:</span> {event.location}
              </div>
            )}

            {/* Recurrence */}
            {(event.isRecurring || event.isRecurrenceInstance) && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#999' }}>Recurrence:</span>{' '}
                {event.recurrencePattern === 'daily' && `Repeats daily`}
                {event.recurrencePattern === 'weekdays' && `Repeats on weekdays`}
                {event.recurrencePattern === 'weekly' && `Repeats weekly`}
                {event.recurrencePattern === 'monthly' && `Repeats monthly`}
                {event.recurrencePattern === 'yearly' && `Repeats yearly`}
                {event.recurrencePattern === 'custom' && `Repeats on selected days`}
              </div>
            )}

            {/* Source info */}
            {(event.eventSource || event.activityType) && (
              <div style={{ marginBottom: '4px', fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#999' }}>
                {event.activityType === 'edited' ? 'Last edited' : 'Added'} {event.timestamp ? formatTimestamp(event.timestamp) : ''}
                {event.eventSource && ` • From ${event.eventSource}`}
              </div>
            )}
          </div>

          {/* Simple text action buttons */}
          <div style={{
            marginTop: '12px',
            display: 'flex',
            gap: '16px',
            fontSize: isMobile ? '0.85rem' : '0.9rem'
          }}>
            {canEditEvents && (
              <button
                onClick={handleEdit}
                style={{
                  backgroundColor: 'transparent',
                  color: '#007bff',
                  border: 'none',
                  padding: '0',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Edit
              </button>
            )}
            {canDeleteEvents && (
              <button
                onClick={handleDelete}
                style={{
                  backgroundColor: 'transparent',
                  color: '#dc3545',
                  border: 'none',
                  padding: '0',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString();
  } catch (e) {
    return '';
  }
};

export default RecentActivityList;